import { TRUSTED_SENDER_STATUS_NOT_CONFIRMED } from 'Api/beSendGoApi'
import getIntl from 'Helpers/intl'
import fetchStatus from 'Hooks/useTrustedEmailSender/fetchStatus'
import {
  checkMessageErrors,
  getAccountEmails,
  getGroups,
  getListDetails,
  getMessageDetails,
  getPecPlusConfigStatus,
  getSmallGroups,
  saveEmailAddresses,
  sendToGroup,
  sendToRecipients
} from 'Pages/QuickSend/recoil/actions/actions'
import {
  ACTUAL_VIEW_RECIPIENTS,
  addToGroupsLoadingState,
  addToGroupsState,
  MAX_EMAIL,
  pageState,
  recipientsState,
  selectState,
  smallGroupsLoadingState,
  smallGroupsState
} from 'Pages/QuickSend/recoil/state/state'
import { regexEmail, STORE_KEY_ACCOUNT, storeGet } from 'mailup-react-helpers'
import { MESSAGE_BOX_VARIANT_DANGER } from 'mailup-web-components'

export const initQuickSendCallback = ({ set }) => async idMessage => {
  set(pageState, prev => ({
    ...prev,
    loading: true,
    loaded: false,
    error: false
  }))

  try {
    const [
      pecPlusStatus,
      messageErrors,
      { Subject, Notes, PreHeader, MessageType, IsBee },
      accountEmails,
      { SenderName, SenderEmail, SenderReplyTo, BouncedEmail }
    ] = await Promise.all([
      getPecPlusConfigStatus(),
      checkMessageErrors(idMessage),
      getMessageDetails(idMessage),
      getAccountEmails(),
      getListDetails()
    ])
    const { GracePeriod } = storeGet(STORE_KEY_ACCOUNT)

    let senderIsDeleted = false
    let waitForVerified = false
    if (SenderEmail === null || SenderEmail === '') {
      senderIsDeleted = true
      waitForVerified = true
    } else {
      const res = await fetchStatus(SenderEmail)
      if (res.status === TRUSTED_SENDER_STATUS_NOT_CONFIRMED) {
        senderIsDeleted = true
        waitForVerified = true
      }
    }

    set(pageState, prev => ({
      ...prev,
      pecPlusStatus,
      messageErrors,
      subject: Subject,
      notes: Notes,
      preHeader: PreHeader,
      messageType: MessageType,
      isBee: IsBee,
      gracePeriod: GracePeriod,
      senderName: SenderName,
      senderEmail: SenderEmail,
      replyToEmail: SenderReplyTo,
      bouncedEmail: BouncedEmail,
      senderIsDeleted,
      waitForVerified
    }))
    let emailAddresses = []
    if (accountEmails) {
      const addr =
        accountEmails.DataValue && accountEmails.DataValue !== ''
          ? typeof JSON.parse(accountEmails.DataValue) === 'string'
            ? []
            : JSON.parse(accountEmails.DataValue).addresses
          : []

      emailAddresses = addr.length
        ? addr.indexOf(';') > -1
          ? addr.split(';') // .filter(e => e !== '')
          : [addr]
        : []
    }

    set(recipientsState, prev => ({
      ...prev,
      emailAddresses
    }))
  } catch (error) {
    set(pageState, prev => ({
      ...prev,
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: MESSAGE_BOX_VARIANT_DANGER
    }))
  }

  set(pageState, prev => ({ ...prev, loading: false, loaded: true }))
}

export const initSmallGroup = ({ set }) => async () => {
  set(smallGroupsLoadingState, prev => ({
    ...prev,
    loading: true,
    loaded: false,
    error: false
  }))
  try {
    const smallGroups = await getSmallGroups()
    set(smallGroupsState, smallGroups)
  } catch (error) {
    set(smallGroupsLoadingState, prev => ({ ...prev, error: true }))
  }
  set(smallGroupsLoadingState, prev => ({
    ...prev,
    loading: false,
    loaded: true
  }))
}

export const initGroup = ({ set }) => async () => {
  set(addToGroupsLoadingState, prev => ({
    ...prev,
    loading: true,
    loaded: false,
    error: false
  }))
  try {
    const groups = await getGroups()
    set(addToGroupsState, groups)
  } catch (error) {
    set(addToGroupsLoadingState, prev => ({ ...prev, error: true }))
  }
  set(addToGroupsLoadingState, prev => ({
    ...prev,
    loading: false,
    loaded: true
  }))
}

export const sendCallback = ({ set, snapshot }) => async idMessage => {
  set(pageState, prev => ({
    ...prev,
    loading: true
  }))
  const { senderName, senderEmail, replyToEmail } = await snapshot.getPromise(
    pageState
  )
  const {
    emailAddresses,
    actualView,
    group: addIdGroup
  } = await snapshot.getPromise(recipientsState)
  const { group: idGroup } = await snapshot.getPromise(selectState)
  let sendResponse

  try {
    await saveEmailAddresses(emailAddresses.filter(e => e !== ''))
    if (actualView === ACTUAL_VIEW_RECIPIENTS) {
      const data = {
        Recipients: emailAddresses.filter(e => e !== ''),
        SenderName: senderName,
        SenderEmail: senderEmail,
        ReplyToEmail: replyToEmail,
        AddToIdGroup: addIdGroup || 0
      }
      sendResponse = await sendToRecipients(idMessage, data)
    } else {
      const data = {
        SenderName: senderName,
        SenderEmail: senderEmail,
        ReplyToEmail: replyToEmail
      }
      sendResponse = await sendToGroup(idMessage, idGroup, data)
    }
    set(pageState, prev => ({
      ...prev,
      sendResponse
    }))
  } catch (error) {
    set(pageState, prev => ({
      ...prev,
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: MESSAGE_BOX_VARIANT_DANGER
    }))
  }
  set(pageState, prev => ({
    ...prev,
    loading: false
  }))
}

export const verifyRecipientEmailsCallback = ({
  set,
  snapshot
}) => async () => {
  const { emailAddresses, emailAddress } = await snapshot.getPromise(
    recipientsState
  )
  const tokenEmails =
    emailAddress.indexOf(';') > -1
      ? emailAddress
          .split(';')
          .map(e => e.trim()) // caso particolare in cui l'utente inserisce spazi bianchi all'inizio o alla fine
          .filter(e => e !== '') // caso particolare in cui l'utente lascia il ; all'inizio o alla fine
      : emailAddress !== ''
      ? [emailAddress.trim()]
      : []
  const eNull = emailAddress === ''

  // lunghezza di tutti e due gli array, quello inserito e quello da inserire
  const eLength =
    emailAddresses.length > MAX_EMAIL ||
    tokenEmails.length > MAX_EMAIL ||
    [...emailAddresses, ...tokenEmails].length > MAX_EMAIL
  // ci sono indirizzi duplicati se la lunghezza dei due array è diversa dalla lunghezza delle somme
  const eDuplicate =
    [...new Set([...emailAddresses, ...tokenEmails])].length !==
    emailAddresses.length + tokenEmails.length
  // se uno degli indirizzi non rispetta la regex allora il risultato è definito
  const eRegex =
    tokenEmails.find(e => e.match(regexEmail) === null) === undefined

  set(recipientsState, prev => ({
    ...prev,
    error: false
  }))

  if (!eNull && !eLength && !eDuplicate && eRegex) {
    const eList = [...emailAddresses, ...tokenEmails]
    set(recipientsState, prev => ({
      ...prev,
      emailAddresses: eList,
      emailAddress: ''
    }))
    return true
  } else {
    // eRegex Se non validi => visualizzazione messaggio danger Load.QuickSend.WrongFormatWarning
    !eRegex &&
      set(recipientsState, prev => ({
        ...prev,
        error: getIntl().formatMessage({
          id: 'Load.QuickSend.WrongFormatWarning'
        })
      }))
    // eDuplicate Se è stato inserito un indirizzo già presente => visualizzazione messaggio danger Load.QuickSend.ErrorEmailDuplicate
    eDuplicate &&
      set(recipientsState, prev => ({
        ...prev,
        error: getIntl().formatMessage({
          id: 'Load.QuickSend.ErrorEmailDuplicate'
        })
      }))
    // eNull Se input vuoto => visualizzazione messaggio danger Load.QuickSend.SpecifyEmailAddress
    eNull &&
      set(recipientsState, prev => ({
        ...prev,
        error: getIntl().formatMessage({
          id: 'Load.QuickSend.SpecifyEmailAddress'
        })
      }))
    // eLength Se sono già stati inseriti 10 indirizzi => visualizzazione messaggio danger Load.QuickSend.LimitEmailWarning
    eLength &&
      set(recipientsState, prev => ({
        ...prev,
        error: getIntl().formatMessage({
          id: 'Load.QuickSend.LimitEmailWarning'
        })
      }))
    return false
  }
}
